import { useState } from "react";
import { Link } from "react-router-dom";
import { supplierApi } from "../../api";
import { ProcError, ProcLoading, ProcPagination, ProcTabs } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, usePageState } from "../../hooks";
import type { SupplierStatus } from "../../types";

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Pending", value: "PENDING" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Delisted", value: "DELISTED" },
];

export default function SupplierListPage() {
  const [status, setStatus] = useState<SupplierStatus | "">("");
  const { page, setPage, state } = usePageState();

  const { data, loading, error } = useAsyncData(
    () => supplierApi.list({ status: status || undefined, page: state.page, size: state.size }),
    [status, state.page, state.size],
  );

  function handleTabChange(value: string) {
    setStatus(value as SupplierStatus | "");
    setPage(0);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Suppliers</h1>
        </div>
        <Link to="/procurement/suppliers/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
          New Supplier
        </Link>
      </div>

      <ProcTabs tabs={STATUS_TABS} active={status} onChange={handleTabChange} />

      {loading && <ProcLoading />}
      {error && <ProcError message={error} />}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Prequalified</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No suppliers found.</td></tr>
                )}
                {data.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-slate-700">{r.code}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.category}</td>
                    <td className="px-4 py-3 text-slate-600">{r.contactPerson || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.phone || "—"}</td>
                    <td className="px-4 py-3"><ProcStatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-center text-lg">{r.prequalified ? "✓" : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <Link to={`/procurement/suppliers/${r.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                      <Link to={`/procurement/suppliers/${r.id}/edit`} className="font-semibold text-blue-600 hover:underline">Edit</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ProcPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
